import {
    ChangePercentage,
    DefaultCellRightAlign,
    IndexCell,
    PercentageBarCellRounded,
    RankCell,
    TimeCell,
    TrafficShare,
} from "app/components/React/Table/cells";
import { RowSelectionConsumer } from "app/components/React/Table/cells/RowSelection";
import { DefaultCellHeader, DefaultEllipsisHeaderCell, HeaderCellBlank } from "app/components/React/Table/headerCells";
import { translate } from "app/filters/i18nFilter";
import type { ITableColumnSort } from "./CategoryShareTableTypes";
import { PagesPerVisit } from "app/components/React/Table/cells/PagesPerVisit";
import { BounceRate } from "app/components/React/Table/cells/BounceRate";
import { AdsenseCell } from "app/components/React/Table/cells/AdsenseCell";
import { UserCustomCategoryService } from "app/services/category/userCustomCategoryService";
import type { RawParams } from "@uirouter/core";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { swSettings } from "app/common/services/swSettings";
import { EPeriodSupport, getSupportPeriod } from "app/UtilitiesAndConstants/UtilityFunctions/periodSupport";
import { SegmentOrDomainCellWithoutDomainSign } from "app/components/React/Table/cells/SegmentOrDomainCell";

const getTableFilters = (params: any, services: { apiHelper: any }) => {
    const isCustomCategory = UserCustomCategoryService.isCustomCategory(params.category);

    const tableParams = {
        ...params,
        includeSubDomains: true,
        timeGranularity: "Monthly",
        categoryHash: isCustomCategory
            ? UserCustomCategoryService.getCategoryHash(
                  UserCustomCategoryService.removeCategoryIdPrefix(params.category),
                  "categoryId",
              )
            : "",
        keys: `$${decodeURIComponent(params.category)}`,
    };
    return services.apiHelper.transformParamsForAPI(tableParams);
};

export const getUrlForTableRow = (row: { Domain: string }, tableParams: RawParams) => {
    const { Domain } = row;
    const { country, duration, webSource } = tableParams;
    const toState = "digitalsuite_website_websiteperformance";
    const navigationParams = {
        key: Domain,
        country,
        duration,
        webSource,
        isWWW: "*",
    };
    const innerLink = proNavigator.href(toState, navigationParams);
    return innerLink;
};

export const getTableColumnsConfig = (
    sortedColumn: ITableColumnSort,
    excludeFields: string[],
    selectedIndustryHasSegments: boolean,
) => {
    const monthlySupport = swSettings.components.IndustryAnalysis.resources.Duration;
    const periodSupport: EPeriodSupport = getSupportPeriod(monthlySupport);
    const periodChangeColumn = periodSupport
        ? {
              field: "PeriodChange",
              displayName:
                  periodSupport === EPeriodSupport.Yearly
                      ? translate("analysis.source.search.all.table.columns.yearly.change.title")
                      : translate("analysis.source.search.all.table.columns.quarterly.change.title"),
              sortable: true,
              groupable: false,
              cellComponent: ChangePercentage,
              tooltip:
                  periodSupport === EPeriodSupport.Yearly
                      ? translate("analysis.source.search.all.table.columns.yearly.change.tooltip")
                      : translate("analysis.source.search.all.table.columns.quarterly.change.tooltip"),
              width: periodSupport === EPeriodSupport.Yearly ? 118 : 130,
          }
        : null;
    const isPeriodChangeShouldAppear = Boolean(periodChangeColumn) && periodSupport !== EPeriodSupport.Monthly;

    return [
        {
            fixed: true,
            cellComponent: RowSelectionConsumer,
            sortable: false,
            isResizable: false,
            width: 33,
            disableHeaderCellHover: true,
            headerComponent: DefaultCellHeader,
        },
        {
            fixed: true,
            cellComponent: IndexCell,
            headerComponent: HeaderCellBlank,
            disableHeaderCellHover: true,
            sortable: false,
            isResizable: false,
            width: 65,
        },
        {
            field: "Domain",
            fixed: true,
            displayName: translate("common.domain"),
            type: "string",
            format: "None",
            sortable: true,
            cellComponent: SegmentOrDomainCellWithoutDomainSign,
            headerComponent: DefaultCellHeader,
            showTotalCount: true,
            tooltip: translate("widget.table.tooltip.topsites.domain"),
            width: 185,
            isResizable: true,
        },
        {
            field: "Share",
            displayName: translate("analysis.source.search.all.table.columns.share.title"),
            type: "string",
            format: "percentagesign",
            fixed: false,
            sortable: true,
            cellComponent: TrafficShare,
            headerComponent: DefaultCellHeader,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.topsites.share"),
            minWidth: 143,
            isResizable: false,
        },
        {
            field: "MoMChange",
            displayName: translate("analysis.source.search.all.table.columns.month.over.month.change.title"),
            type: "double",
            format: "number",
            fixed: false,
            sortable: true,
            headerComponent: DefaultEllipsisHeaderCell,
            cellComponent: ChangePercentage,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.topsites.change"),
            width: 158,
            isResizable: true,
        },
        !selectedIndustryHasSegments && {
            field: "Rank",
            displayName: translate("common.tables.columns.country.rank.title"),
            fixed: false,
            sortable: true,
            type: "long",
            format: "swRank",
            headerComponent: DefaultEllipsisHeaderCell,
            cellComponent: RankCell,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.topsites.rank"),
            width: 130,
            isResizable: true,
            inverted: true,
        },
        {
            field: "AvgMonthVisits",
            displayName: translate("common.tables.columns.monthly.visits.title"),
            type: "double",
            format: "minVisitsAbbr",
            fixed: false,
            sortable: true,
            headerComponent: DefaultEllipsisHeaderCell,
            cellComponent: DefaultCellRightAlign,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.monthlyvisits"),
            width: 130,
            isResizable: true,
        },
        {
            field: "UniqueUsers",
            displayName: translate("industry.trends.table.columns.unique.users"),
            type: "double",
            format: "minVisitsAbbr",
            fixed: false,
            sortable: true,
            headerComponent: DefaultEllipsisHeaderCell,
            cellComponent: DefaultCellRightAlign,
            showTotalCount: false,
            tooltip: translate("industry.trends.table.columns.unique.users.tooltip"),
            width: 155,
            isResizable: true,
        },
        isPeriodChangeShouldAppear && periodChangeColumn,
        {
            field: "DesktopMobileShare",
            displayName: translate("common.tables.columns.desktop.mobile.share.title"),
            fixed: false,
            sortable: false,
            type: "double",
            format: "abbrNumber",
            headerComponent: DefaultEllipsisHeaderCell,
            cellComponent: PercentageBarCellRounded,
            showTotalCount: false,
            tooltip: translate("wa.ao.desktopvsmobile.tooltip"),
            minWidth: 190,
            isResizable: true,
            webSources: ["Total"],
        },
        {
            field: "AvgVisitDuration",
            displayName: translate("common.tables.columns.visit.duration.title"),
            type: "double",
            format: "number",
            fixed: false,
            sortable: true,
            headerComponent: DefaultEllipsisHeaderCell,
            cellComponent: TimeCell,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.topsites.avgvisitduration"),
            width: 130,
            isResizable: true,
        },
        {
            field: "PagesPerVisit",
            displayName: translate("common.tables.columns.page.per.visit.title"),
            fixed: false,
            sortable: true,
            type: "double",
            format: "number",
            headerComponent: DefaultEllipsisHeaderCell,
            cellComponent: PagesPerVisit,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.topsites.ppv"),
            width: 120,
            isResizable: true,
        },
        {
            field: "BounceRate",
            displayName: translate("common.tables.columns.bounce.rate.title"),
            type: "double",
            format: "number",
            fixed: false,
            sortable: true,
            headerComponent: DefaultEllipsisHeaderCell,
            cellComponent: BounceRate,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.topsites.bouncerate"),
            width: 125,
            isResizable: true,
            inverted: true,
        },
        {
            field: "HasAdsense",
            displayName: translate("common.tables.columns.adsense.title"),
            type: "bool",
            format: "None",
            fixed: false,
            sortable: true,
            headerComponent: DefaultEllipsisHeaderCell,
            cellComponent: AdsenseCell,
            showTotalCount: false,
            width: 100,
            isResizable: true,
        },
    ]
        .filter((column) => !!column && !excludeFields.includes(column.field))
        .map((col) => {
            const isSorted = sortedColumn && col.field === sortedColumn.field;
            return {
                ...col,
                visible: true,
                isSorted,
                sortDirection: isSorted ? sortedColumn.sortDirection : "desc",
            };
        });
};
